/**
 * Cloud AI Service
 * Client-side service to call Firebase Cloud Functions for AI meal analysis
 *
 * This replaces direct Gemini/OpenAI calls with secure server-side processing.
 * API keys are stored in Firebase Functions config, not in the client.
 */

import { format } from 'date-fns';
import { Firebase } from './firebase/init';
import type { GlycemicAnalysis, ChatMessage } from '@/types/meal';
import type { ImportRecipeRequest, ImportRecipeResponse } from '@/types/recipe';

// ============================================
// Scan limit / chat limit / busy error handling
// ============================================

/**
 * Error code attached to analysis responses when the server rejects the
 * request because the free user is over the daily scan limit
 * (`resource-exhausted` + details.reason === 'scan-limit'). UI layers can
 * detect it via `isScanLimitError(result)` or
 * `result.code === SCAN_LIMIT_ERROR_CODE`.
 */
export const SCAN_LIMIT_ERROR_CODE = 'scan-limit' as const;

/**
 * Error code attached to followUp responses when the server rejects the
 * request because the free user is over the follow-up chat message limit
 * (`resource-exhausted` + details.reason === 'chat-limit').
 */
export const CHAT_LIMIT_ERROR_CODE = 'chat-limit' as const;

/**
 * Error code attached to responses when the AI backend itself is rate
 * limited / overloaded (Gemini 429 → `resource-exhausted` WITHOUT a
 * 'scan-limit'/'chat-limit' reason). Retryable — UI must show a "try again"
 * alert, NOT the paywall. Localized in UI via `errors.serviceBusy`.
 */
export const SERVICE_BUSY_ERROR_CODE = 'service-busy' as const;

/** English fallback for the busy state (UI layers should use t('errors.serviceBusy')). */
const SERVICE_BUSY_MESSAGE = 'The service is busy right now. Please try again in a moment.';

/**
 * RNFirebase surfaces callable error codes UNPREFIXED (e.g.
 * 'resource-exhausted', verified in @react-native-firebase/functions
 * lib/index.js → HttpsErrorCode). We keep the 'functions/'-prefixed form as a
 * fallback alternation for safety (web SDK / future RNFirebase changes).
 */
function hasErrorCode(error: any, code: string): boolean {
  return error?.code === code || error?.code === `functions/${code}`;
}

/**
 * Classify a raw `resource-exhausted` callable error.
 *
 * Server contract:
 * - Scan functions throw HttpsError('resource-exhausted', msg, { reason: 'scan-limit' })
 * - followUp throws with { reason: 'chat-limit' }
 * - Gemini-busy 429s throw resource-exhausted WITHOUT a reason (or reason 'busy')
 *
 * RNFirebase exposes `error.details` directly on the caught HttpsError
 * (verified end-to-end: iOS RNFBFunctionsModule.m and Android
 * UniversalFirebaseFunctionsModule.java both forward `details` in userInfo,
 * and lib/HttpsError.js defines a `details` property). So an untagged
 * resource-exhausted error genuinely means "backend busy".
 * Tradeoff (documented): if `details` were ever dropped in transit, a real
 * scan-limit rejection would surface as a retryable "busy" alert instead of
 * the paywall — the user still cannot scan, so this fails safe.
 *
 * Returns 'scan-limit' | 'chat-limit' | 'service-busy' | null (not a
 * resource-exhausted error at all).
 */
function classifyResourceExhausted(
  error: any
): typeof SCAN_LIMIT_ERROR_CODE | typeof CHAT_LIMIT_ERROR_CODE | typeof SERVICE_BUSY_ERROR_CODE | null {
  if (!hasErrorCode(error, 'resource-exhausted')) return null;
  const reason = error?.details?.reason;
  if (reason === SCAN_LIMIT_ERROR_CODE) return SCAN_LIMIT_ERROR_CODE;
  if (reason === CHAT_LIMIT_ERROR_CODE) return CHAT_LIMIT_ERROR_CODE;
  return SERVICE_BUSY_ERROR_CODE;
}

/**
 * Returns true when the given error/response represents the server-side
 * daily scan limit being reached. Accepts both processed response shapes
 * (code === 'scan-limit') and raw callable errors (unprefixed OR
 * 'functions/'-prefixed resource-exhausted). For raw errors, an explicit
 * different reason ('busy' / 'chat-limit') rules scan-limit out; a missing
 * reason still counts (legacy servers threw untagged scan-limit errors).
 */
export function isScanLimitError(e: any): boolean {
  if (!e) return false;
  if (e.code === SCAN_LIMIT_ERROR_CODE) return true;
  if (!hasErrorCode(e, 'resource-exhausted')) return false;
  const reason = e?.details?.reason;
  return reason == null || reason === SCAN_LIMIT_ERROR_CODE;
}

/**
 * Returns true when the given error/response represents the server-side
 * free-tier follow-up chat limit being reached. Symmetric with
 * `isScanLimitError`: accepts processed response shapes
 * (code === 'chat-limit') and raw callable errors carrying
 * details.reason === 'chat-limit'.
 */
export function isChatLimitError(e: any): boolean {
  if (!e) return false;
  if (e.code === CHAT_LIMIT_ERROR_CODE) return true;
  if (!hasErrorCode(e, 'resource-exhausted')) return false;
  return e?.details?.reason === CHAT_LIMIT_ERROR_CODE;
}

/** Local calendar date for the server-side daily scan limit window */
function getClientDate(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

// ============================================
// Request/Response Types
// ============================================

export interface CloudAnalyzeMealRequest {
  imageBase64: string;
  userLanguage?: string;
  additionalContext?: string;
  /** Local 'YYYY-MM-DD' — used by the server for the daily scan-limit window */
  clientDate?: string;
}

export interface CloudAnalyzeMealResponse {
  success: boolean;
  analysis?: GlycemicAnalysis;
  message?: string;
  suggestions?: string[];
  followUpQuestions?: string[];
  error?: string;
  /** Machine-readable error code (e.g. 'scan-limit') */
  code?: string;
  // Non-food detection
  notFood?: boolean;
  notFoodReason?: string;
}

export interface CloudFollowUpRequest {
  mealId?: string;
  recipeId?: string;
  userQuestion: string;
  conversationHistory: ChatMessage[];
  userLanguage?: string;
}

export interface CloudFollowUpResponse {
  success: boolean;
  message?: string;
  suggestions?: string[];
  error?: string;
  /** Machine-readable error code (e.g. 'chat-limit', 'service-busy') */
  code?: string;
}

export interface CloudAnalyzeLabelRequest {
  imageBase64: string;
  userLanguage?: string;
  /** Local 'YYYY-MM-DD' — used by the server for the daily scan-limit window */
  clientDate?: string;
}

export interface CloudLookupBarcodeRequest {
  barcode: string;
  userLanguage?: string;
  /** Local 'YYYY-MM-DD' — used by the server for the daily scan-limit window */
  clientDate?: string;
}

export interface CloudLookupBarcodeResponse {
  success: boolean;
  found?: boolean;
  analysis?: any;
  message?: string;
  followUpQuestions?: string[];
  error?: string;
  /** Machine-readable error code (e.g. 'scan-limit') */
  code?: string;
  productName?: string;
}

export interface CloudAnalyzeMealTextRequest {
  input: string;
  userLanguage?: string;
  /** Local 'YYYY-MM-DD' — used by the server for the daily scan-limit window */
  clientDate?: string;
}

export interface CloudAnalyzeMealTextResponse {
  success: boolean;
  analysis?: GlycemicAnalysis;
  message?: string;
  suggestions?: string[];
  followUpQuestions?: string[];
  error?: string;
  /** Machine-readable error code (e.g. 'scan-limit') */
  code?: string;
  // Non-food detection (text analysis can also reject non-food descriptions)
  notFood?: boolean;
  notFoodReason?: string;
}

export interface GlycemicInsight {
  type: 'trigger' | 'safe' | 'stacking' | 'timing' | 'trend' | 'swap_opportunity' | 'tip'
    | 'achievement' | 'fiber_insight' | 'sugar_insight' | 'target_progress' | 'meal_suggestion' | 'medication_aware'
    | 'glucose_pattern' | 'hydration' | 'blood_pressure' | 'weight_trend' | 'micronutrient';
  section?: 'highlights' | 'patterns' | 'nutrition' | 'health' | 'recommendations';
  emoji: string;
  title: string;
  description: string;
  confidence?: 'high' | 'medium' | 'low';
}

export interface CloudGenerateInsightsRequest {
  userLanguage?: string;
  days?: number;
  limit?: number;
}

export interface CloudGenerateInsightsResponse {
  success: boolean;
  summary?: string;
  insights: GlycemicInsight[];
  mealsAnalyzed: number;
  generatedAt: string;
  useLocal?: boolean;
  error?: string;
}

// ============================================
// Cloud AI Service
// ============================================

class CloudAIService {
  private analyzeMealFn = Firebase.functions.httpsCallable('analyzeMeal');
  private analyzeMealTextFn = Firebase.functions.httpsCallable('analyzeMealText');
  private analyzeLabelFn = Firebase.functions.httpsCallable('analyzeLabel');
  private lookupBarcodeFn = Firebase.functions.httpsCallable('lookupBarcode');
  private followUpFn = Firebase.functions.httpsCallable('followUp');
  private generateGlycemicInsightsFn = Firebase.functions.httpsCallable('generateGlycemicInsights');
  private importRecipeFn = Firebase.functions.httpsCallable('importRecipe');

  // Demo functions (unauthenticated - for onboarding trial)
  private analyzeMealDemoFn = Firebase.functions.httpsCallable('analyzeMealDemo');
  private analyzeMealTextDemoFn = Firebase.functions.httpsCallable('analyzeMealTextDemo');

  /**
   * Analyze a meal photo using the cloud function
   * @param request - Image data and context
   * @returns Analysis result with glycemic impact breakdown
   */
  async analyzeMeal(request: CloudAnalyzeMealRequest): Promise<CloudAnalyzeMealResponse> {
    try {
      const result = await this.analyzeMealFn({ ...request, clientDate: getClientDate() });
      return result.data as CloudAnalyzeMealResponse;
    } catch (error: any) {
      console.error('Cloud analyzeMeal error:', error);

      // Extract error message from Firebase function error
      const errorMessage = error.message || 'Failed to analyze meal. Please try again.';

      // Check for specific error codes
      if (hasErrorCode(error, 'unauthenticated')) {
        return {
          success: false,
          error: 'Please sign in to analyze meals.',
        };
      }

      const exhaustedKind = classifyResourceExhausted(error);
      if (exhaustedKind === SCAN_LIMIT_ERROR_CODE) {
        // Server-side daily scan limit reached (free users: 3 scans/day)
        return {
          success: false,
          code: SCAN_LIMIT_ERROR_CODE,
          error: errorMessage,
        };
      }
      if (exhaustedKind) {
        // Gemini/backend rate limited — retryable, NOT a paywall situation
        return {
          success: false,
          code: SERVICE_BUSY_ERROR_CODE,
          error: SERVICE_BUSY_MESSAGE,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Analyze a meal from text/voice description using the cloud function
   * @param request - Text description and language
   * @returns Analysis result with glycemic breakdown
   */
  async analyzeMealText(request: CloudAnalyzeMealTextRequest): Promise<CloudAnalyzeMealTextResponse> {
    try {
      const result = await this.analyzeMealTextFn({ ...request, clientDate: getClientDate() });
      return result.data as CloudAnalyzeMealTextResponse;
    } catch (error: any) {
      console.error('Cloud analyzeMealText error:', error);

      const errorMessage = error.message || 'Failed to analyze meal. Please try again.';

      if (hasErrorCode(error, 'unauthenticated')) {
        return {
          success: false,
          error: 'Please sign in to analyze meals.',
        };
      }

      const exhaustedKind = classifyResourceExhausted(error);
      if (exhaustedKind === SCAN_LIMIT_ERROR_CODE) {
        // Server-side daily scan limit reached (free users: 3 scans/day)
        return {
          success: false,
          code: SCAN_LIMIT_ERROR_CODE,
          error: errorMessage,
        };
      }
      if (exhaustedKind) {
        // Gemini/backend rate limited — retryable, NOT a paywall situation
        return {
          success: false,
          code: SERVICE_BUSY_ERROR_CODE,
          error: SERVICE_BUSY_MESSAGE,
        };
      }

      if (hasErrorCode(error, 'invalid-argument')) {
        return {
          success: false,
          error: errorMessage,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Analyze a nutrition label photo using the cloud function
   * @param request - Image data and language
   * @returns Analysis result with glycemic impact breakdown
   */
  async analyzeLabel(request: CloudAnalyzeLabelRequest): Promise<CloudAnalyzeMealResponse> {
    try {
      const result = await this.analyzeLabelFn({ ...request, clientDate: getClientDate() });
      return result.data as CloudAnalyzeMealResponse;
    } catch (error: any) {
      console.error('Cloud analyzeLabel error:', error);

      const errorMessage = error.message || 'Failed to analyze label. Please try again.';

      if (hasErrorCode(error, 'unauthenticated')) {
        return {
          success: false,
          error: 'Please sign in to analyze labels.',
        };
      }

      const exhaustedKind = classifyResourceExhausted(error);
      if (exhaustedKind === SCAN_LIMIT_ERROR_CODE) {
        // Server-side daily scan limit reached (free users: 3 scans/day)
        return {
          success: false,
          code: SCAN_LIMIT_ERROR_CODE,
          error: errorMessage,
        };
      }
      if (exhaustedKind) {
        // Gemini/backend rate limited — retryable, NOT a paywall situation
        return {
          success: false,
          code: SERVICE_BUSY_ERROR_CODE,
          error: SERVICE_BUSY_MESSAGE,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Look up a product by barcode and return glycemic analysis
   * @param request - Barcode string and language
   * @returns Barcode lookup result with analysis if found
   */
  async lookupBarcode(request: CloudLookupBarcodeRequest): Promise<CloudLookupBarcodeResponse> {
    try {
      const result = await this.lookupBarcodeFn({ ...request, clientDate: getClientDate() });
      return result.data as CloudLookupBarcodeResponse;
    } catch (error: any) {
      console.error('Cloud lookupBarcode error:', error);

      const errorMessage = error.message || 'Failed to look up barcode. Please try again.';

      if (hasErrorCode(error, 'unauthenticated')) {
        return {
          success: false,
          error: 'Please sign in to scan barcodes.',
        };
      }

      const exhaustedKind = classifyResourceExhausted(error);
      if (exhaustedKind === SCAN_LIMIT_ERROR_CODE) {
        // Server-side daily scan limit reached (free users: 3 scans/day)
        return {
          success: false,
          code: SCAN_LIMIT_ERROR_CODE,
          error: errorMessage,
        };
      }
      if (exhaustedKind) {
        // Gemini/backend rate limited — retryable, NOT a paywall situation
        return {
          success: false,
          code: SERVICE_BUSY_ERROR_CODE,
          error: SERVICE_BUSY_MESSAGE,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Handle follow-up questions about a meal
   * @param request - Question and conversation context
   * @returns AI response
   */
  async followUp(request: CloudFollowUpRequest): Promise<CloudFollowUpResponse> {
    try {
      const result = await this.followUpFn(request);
      return result.data as CloudFollowUpResponse;
    } catch (error: any) {
      console.error('Cloud followUp error:', error);

      const errorMessage = error.message || 'Failed to process your question. Please try again.';

      if (hasErrorCode(error, 'unauthenticated')) {
        return {
          success: false,
          error: 'Please sign in to continue chatting.',
        };
      }

      if (hasErrorCode(error, 'not-found')) {
        return {
          success: false,
          error: 'Meal not found.',
        };
      }

      const exhaustedKind = classifyResourceExhausted(error);
      if (exhaustedKind === CHAT_LIMIT_ERROR_CODE) {
        // Server-enforced free-tier follow-up message limit reached
        return {
          success: false,
          code: CHAT_LIMIT_ERROR_CODE,
          error: errorMessage,
        };
      }
      if (exhaustedKind) {
        // Gemini/backend rate limited — retryable, NOT a paywall situation
        return {
          success: false,
          code: SERVICE_BUSY_ERROR_CODE,
          error: SERVICE_BUSY_MESSAGE,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  // ============================================
  // Demo Methods (Unauthenticated - for Onboarding Trial)
  // ============================================

  /**
   * Analyze a meal photo using the demo cloud function (no auth required)
   * @param request - Image data and context
   * @returns Analysis result
   */
  async analyzeMealDemo(request: CloudAnalyzeMealRequest): Promise<CloudAnalyzeMealResponse> {
    try {
      const result = await this.analyzeMealDemoFn(request);
      return result.data as CloudAnalyzeMealResponse;
    } catch (error: any) {
      console.error('Cloud analyzeMealDemo error:', error);

      const errorMessage = error.message || 'Failed to analyze meal. Please try again.';

      if (hasErrorCode(error, 'resource-exhausted')) {
        return {
          success: false,
          code: SERVICE_BUSY_ERROR_CODE,
          error: SERVICE_BUSY_MESSAGE,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Analyze a meal from text/voice description using the demo cloud function (no auth required)
   * @param request - Text description and language
   * @returns Analysis result
   */
  async analyzeMealTextDemo(request: CloudAnalyzeMealTextRequest): Promise<CloudAnalyzeMealTextResponse> {
    try {
      const result = await this.analyzeMealTextDemoFn(request);
      return result.data as CloudAnalyzeMealTextResponse;
    } catch (error: any) {
      console.error('Cloud analyzeMealTextDemo error:', error);

      const errorMessage = error.message || 'Failed to analyze meal. Please try again.';

      if (hasErrorCode(error, 'resource-exhausted')) {
        return {
          success: false,
          code: SERVICE_BUSY_ERROR_CODE,
          error: SERVICE_BUSY_MESSAGE,
        };
      }

      if (hasErrorCode(error, 'invalid-argument')) {
        return {
          success: false,
          error: errorMessage,
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  // ============================================
  // AI Insights Generation
  // ============================================

  /**
   * Generate personalized glycemic insights from meal history
   * @param request - Language and optional filters
   * @returns AI-generated insights
   */
  async generateGlycemicInsights(request: CloudGenerateInsightsRequest = {}): Promise<CloudGenerateInsightsResponse> {
    try {
      // Check if user is authenticated before calling
      const currentUser = Firebase.auth.currentUser;
      console.log('[CloudAI] Current user:', currentUser?.uid || 'NONE');

      if (!currentUser) {
        console.log('[CloudAI] No authenticated user, using local fallback');
        return {
          success: true,
          insights: [],
          mealsAnalyzed: 0,
          generatedAt: new Date().toISOString(),
          useLocal: true,
        };
      }

      // Force refresh the ID token to ensure it's valid
      try {
        const token = await currentUser.getIdToken(true);
        console.log('[CloudAI] Got fresh token:', token.substring(0, 20) + '...');
      } catch (tokenError) {
        console.log('[CloudAI] Failed to get token:', tokenError);
      }

      console.log('[CloudAI] Calling generateGlycemicInsights...');
      const result = await this.generateGlycemicInsightsFn(request);
      console.log('[CloudAI] Success:', result.data);
      return result.data as CloudGenerateInsightsResponse;
    } catch (error: any) {
      console.log('[CloudAI] Error:', error.code, error.message);
      // Silently fall back to local for auth errors (expected when auth not ready)
      const errorCode = error.code || '';
      const errorMessage = error.message || '';
      const isAuthError = errorCode.includes('unauthenticated') ||
                          errorMessage.includes('UNAUTHENTICATED') ||
                          errorMessage.includes('unauthenticated');

      if (isAuthError) {
        console.log('[CloudAI] Auth error, falling back to local');
        return {
          success: true,
          insights: [],
          mealsAnalyzed: 0,
          generatedAt: new Date().toISOString(),
          useLocal: true,
        };
      }

      console.warn('Cloud generateGlycemicInsights error:', errorMessage);

      // For any other error, fall back to local insights
      return {
        success: true,
        insights: [],
        mealsAnalyzed: 0,
        generatedAt: new Date().toISOString(),
        useLocal: true,
        error: errorMessage,
      };
    }
  }

  // ============================================
  // Recipe Import
  // ============================================

  /**
   * Import a recipe from URL and analyze for glycemic impact
   * @param request - URL and language
   * @returns Imported recipe with glycemic analysis
   */
  async importRecipe(request: ImportRecipeRequest): Promise<ImportRecipeResponse> {
    try {
      const result = await this.importRecipeFn(request);
      return result.data as ImportRecipeResponse;
    } catch (error: any) {
      console.error('Cloud importRecipe error:', error);

      const errorMessage = error.message || 'Failed to import recipe. Please try again.';

      if (hasErrorCode(error, 'unauthenticated')) {
        return {
          success: false,
          error: 'Please sign in to import recipes.',
        };
      }

      if (hasErrorCode(error, 'permission-denied')) {
        return {
          success: false,
          error: 'Recipe import is a premium feature.',
        };
      }

      if (hasErrorCode(error, 'invalid-argument')) {
        return {
          success: false,
          error: errorMessage,
        };
      }

      if (hasErrorCode(error, 'deadline-exceeded')) {
        return {
          success: false,
          error: 'The website took too long to respond. Please try again.',
        };
      }

      if (hasErrorCode(error, 'unavailable')) {
        return {
          success: false,
          error: 'Could not access the website. Please check the URL and try again.',
        };
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }
}

// Export singleton instance
export const CloudAI = new CloudAIService();
